import { useState, useEffect, useCallback } from "react";
import { getAllProducts, createProduct as sbCreateProduct, updateProduct as sbUpdateProduct, deleteProduct as sbDeleteProduct } from "@/services/supabaseService";
import type { Product as SbProduct } from "@/lib/supabase";
import type { Product } from "@/mocks/products";

function mapSbToProduct(p: SbProduct): Product {
  return {
    id: p.id,
    name: p.name,
    region: p.region || "全部商品",
    category: p.category,
    costPrice: Number(p.cost_price),
    distributionPrice: Number(p.distribution_price),
    normalPrice: Number((p as Record<string, unknown>).normal_price ?? p.distribution_price),
    corePrice: Number(p.core_price),
    dealerPrice: Number(p.dealer_price),
    suggestedPrice: Number(p.suggested_price),
    stock: p.stock,
    images: Array.isArray(p.images) ? (p.images as string[]) : [],
    description: p.description || "",
    isHot: p.is_hot,
    isNew: p.is_new,
    isOnSale: p.is_on_sale,
    tags: Array.isArray(p.tags) ? (p.tags as string[]) : [],
  };
}

function mapProductToSb(p: Product): Omit<SbProduct, "id" | "created_at" | "updated_at"> & { id?: string; normal_price?: number } {
  return {
    id: p.id,
    name: p.name,
    region: p.region || "全部商品",
    category: p.category,
    cost_price: p.costPrice,
    distribution_price: p.distributionPrice,
    normal_price: p.normalPrice,
    core_price: p.corePrice,
    dealer_price: p.dealerPrice,
    suggested_price: p.suggestedPrice,
    stock: p.stock,
    images: p.images,
    description: p.description,
    is_hot: p.isHot,
    is_new: p.isNew,
    is_on_sale: p.isOnSale,
    tags: p.tags,
  };
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllProducts();
      if (data.length > 0) {
        setProducts(data.map(mapSbToProduct));
      }
    } catch {
      // silently ignore if table is empty or not connected yet
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const addProduct = async (product: Product) => {
    const sbData = mapProductToSb(product);

    // ALWAYS remove id for create — let DB auto-generate UUID
    delete (sbData as Record<string, unknown>).id;

    // Sanitize numeric fields: ensure valid numbers, default 0
    const numericFields = [
      "cost_price",
      "distribution_price",
      "normal_price",
      "core_price",
      "dealer_price",
      "suggested_price",
      "stock",
    ] as const;
    for (const key of numericFields) {
      const val = (sbData as Record<string, unknown>)[key];
      if (val === undefined || val === null || val === "" || Number.isNaN(Number(val))) {
        (sbData as Record<string, unknown>)[key] = 0;
      }
    }

    // Sanitize images: filter out empty strings, provide fallback
    const images = Array.isArray(sbData.images)
      ? sbData.images.filter((img: string) => img && img.trim())
      : [];
    if (images.length === 0) {
      images.push(
        "https://readdy.ai/api/search-image?query=generic%20product%20placeholder%20white%20background%20minimal%20clean%20photography&width=400&height=400&seq=newprod&orientation=squarish"
      );
    }
    sbData.images = images;

    // Ensure tags is always an array
    if (!Array.isArray(sbData.tags)) {
      sbData.tags = [];
    }

    try {
      await sbCreateProduct(sbData);
      await fetchProducts();
    } catch (err) {
      throw err;
    }
  };

  const updateProduct = async (product: Product) => {
    const sbData = mapProductToSb(product);

    // Sanitize numeric fields
    const numericFields = [
      "cost_price",
      "distribution_price",
      "normal_price",
      "core_price",
      "dealer_price",
      "suggested_price",
      "stock",
    ] as const;
    for (const key of numericFields) {
      const val = (sbData as Record<string, unknown>)[key];
      if (val === undefined || val === null || val === "" || Number.isNaN(Number(val))) {
        (sbData as Record<string, unknown>)[key] = 0;
      }
    }

    // Sanitize images
    const images = Array.isArray(sbData.images)
      ? sbData.images.filter((img: string) => img && img.trim())
      : [];
    if (images.length === 0) {
      images.push(
        "https://readdy.ai/api/search-image?query=generic%20product%20placeholder%20white%20background%20minimal%20clean%20photography&width=400&height=400&seq=newprod&orientation=squarish"
      );
    }
    sbData.images = images;

    if (!Array.isArray(sbData.tags)) {
      sbData.tags = [];
    }

    try {
      await sbUpdateProduct(product.id, sbData);
      await fetchProducts();
    } catch (err) {
      throw err;
    }
  };

  const toggleProductStatus = async (productId: string) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    const updated = { ...p, stock: p.stock > 0 ? -1 : 100 };
    await updateProduct(updated);
  };

  const removeProduct = async (productId: string) => {
    try {
      await sbDeleteProduct(productId);
      await fetchProducts();
    } catch {
      // fallback
    }
  };

  return {
    products,
    isLoading,
    addProduct,
    updateProduct,
    toggleProductStatus,
    removeProduct,
    refresh: fetchProducts,
  };
}